import CloseIcon from '@mui/icons-material/Close'
import { Box } from '@mui/material'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import LabelComponent from 'features/solicitudes/components/LabelComponent'
import ValidatingText from 'features/solicitudes/components/ValidatingText'
import {
  consultaInicial,
  executeFieldAction,
  selectCamposSolicitud,
  selectDatosSolicitud,
  setCampoCargando,
  setCampoError,
  setCampoValidando,
} from 'features/solicitudes/solicitudesSlice'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { numericValidation, validate } from '../../libs/FieldResquestValidator'
import { processValidationResult, todosLosPadresTienenValor } from '../../libs/SolicitudesHelpers'

function TextComponent({ idpk }) {
  const inputReference = useRef(null)
  const dispatch = useDispatch()
  const campos = useSelector(selectCamposSolicitud)
  const campo = campos.filter((c) => c.idpk === idpk)[0]

  const datos = useSelector(selectDatosSolicitud)
  const valor = datos[campo.idpk]

  const [value, setValue] = useState(campo.valor_dfto ?? '')
  const [validationError, setValidationError] = useState('')
  const [alertOpen, setAlertOpen] = useState(false)

  const getValoresConsulta = () => {
    const valores = []
    for (const idpk in datos) {
      if (Object.hasOwnProperty.call(datos, idpk)) {
        const valor = datos[idpk]
        const field = campos.filter((c) => Number(c.idpk) === Number(idpk))[0]
        valores.push({ codigo_campo: field.codigo_campo, valor, tipo: field.tipo_dato })
      }
    }
    return valores
  }

  // La inicialización ahora se hace de forma centralizada en CamposSolicitud
  // mediante el endpoint /inicializasolicitud/

  useEffect(() => {
    setValue(valor ?? '')
    if (valor) {
      dispatch(setCampoCargando({ idpk: campo.idpk, cargando: false }))
    }
    // Lanzamos las queries iniciales para campos dependientes solo si:
    // 1. El campo tiene valor
    // 2. El campo dependiente necesita inicialización (inicializando === 'P')
    // 3. El campo dependiente NO está siendo inicializado por inicializaSolicitud (inicializando !== 'R')
    campos.forEach((c) => {
      if (
        c.depende_de &&
        c.depende_de.includes(campo.codigo_campo) &&
        valor &&
        c.inicializando === 'P' &&
        todosLosPadresTienenValor(c, campos, datos)
      ) {
        const valores = getValoresConsulta()
        dispatch(consultaInicial({ idpk: c.idpk, valores }))
      }
    })
    campo.acciones.forEach((action) => {
      if (
        action.valor_referencia === valor ||
        (action.valor_referencia === 'NOT_NULL' && valor) ||
        (action.valor_referencia === 'NULL' && !valor)
      ) {
        dispatch(executeFieldAction({ action }))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor])

  const handleError = (open, message, idpk) => {
    setAlertOpen(open)
    setValidationError(message)
    dispatch(setCampoError({ idpk: idpk, error: open }))
  }

  const handleBlur = async (e) => {
    try {
      let value = e.target.value

      if (campo.tipo_dato === 'N') {
        value = numericValidation(campo, e.target.value, true)
      }

      // Activar estado de validación
      dispatch(setCampoValidando({ idpk: campo.idpk, validando: true }))

      const newValue = await validate(campo, value, campos, datos)
      processValidationResult(newValue, campo, value, dispatch)
      handleError(false, '', campo.idpk)
    } catch (error) {
      handleError(true, error.message, campo.idpk)
      // Intentar hacer focus solo si las referencias aún existen
      if (inputReference.current) {
        inputReference.current.focus()
      } else if (e.currentTarget) {
        e.currentTarget.focus()
      } else if (e.target) {
        e.target.focus()
      }
    } finally {
      // Desactivar estado de validación
      dispatch(setCampoValidando({ idpk: campo.idpk, validando: false }))
    }
  }

  /**
   * This function will handle the sanitization of the numeric input:
   *     - Change decimal separator
   *     - Check the masking
   */
  const handleNumericChange = (e) => {
    try {
      const masked_value = numericValidation(campo, e.target.value)
      setValue(masked_value)
      handleError(false, '', campo.idpk)
    } catch (e) {
      handleError(true, e.message, campo.idpk)
    }
  }

  /**@typedef {CampoSolicitud} campo */
  if (campo) {
    const numerico = (
      <Box>
        <LabelComponent text={campo.etiqueta} obligatorio={campo.obligatorio} />
        <TextField
          ref={inputReference}
          key={campo.idpk}
          id={campo.codigo_campo}
          placeholder={campo.etiqueta}
          variant="standard"
          required={campo.obligatorio === 'S'}
          helperText={campo.validando ? <ValidatingText /> : campo.ayuda}
          fullWidth
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
          InputLabelProps={{ shrink: value ? true : false }}
          value={value}
          onChange={handleNumericChange}
          onBlur={handleBlur}
          InputProps={{
            startAdornment: campo.cargando ? <CircularProgress size={20} /> : null,
          }}
          error={campo.error ? true : false}
          disabled={campo.informativo === 'S' || campo.validando}
        />
      </Box>
    )
    const alfanumerico = (
      <Box>
        <LabelComponent text={campo.etiqueta} obligatorio={campo.obligatorio} />
        <TextField
          ref={inputReference}
          key={campo.idpk}
          id={campo.codigo_campo}
          placeholder={campo.etiqueta}
          variant="standard"
          required={campo.obligatorio === 'S'}
          helperText={campo.validando ? <ValidatingText /> : campo.ayuda}
          fullWidth
          InputLabelProps={{ shrink: value ? true : false }}
          value={value}
          onChange={(event) => {
            setValue(event.target.value)
          }}
          onBlur={handleBlur}
          InputProps={{
            startAdornment: campo.cargando ? <CircularProgress size={20} /> : null,
          }}
          error={campo.error ? true : false}
          disabled={campo.informativo === 'S' || campo.validando}
        />
      </Box>
    )
    const textArea = (
      <Box>
        <LabelComponent text={campo.etiqueta} obligatorio={campo.obligatorio} />
        <TextField
          ref={inputReference}
          key={campo.idpk}
          id={campo.codigo_campo}
          placeholder={campo.etiqueta}
          variant="standard"
          required={campo.obligatorio === 'S'}
          helperText={campo.validando ? <ValidatingText /> : campo.ayuda}
          fullWidth
          multiline
          rows={4}
          InputLabelProps={{ shrink: value ? true : false }}
          value={value}
          onChange={(event) => {
            setValue(event.target.value)
          }}
          onBlur={handleBlur}
          InputProps={{
            startAdornment: campo.cargando ? <CircularProgress size={20} /> : null,
          }}
          error={campo.error ? true : false}
          disabled={campo.informativo === 'S' || campo.validando}
        />
      </Box>
    )
    const validationAlert = (
      <Collapse sx={{ mt: 2 }} in={alertOpen}>
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setAlertOpen(false)
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 2 }}
          severity="error"
        >
          <AlertTitle>Error</AlertTitle>
          {validationError}
        </Alert>
      </Collapse>
    )

    if (campo.tipo_dato === 'N') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {numerico}
          {validationAlert}
        </Box>
      )
    } else if (Number(campo.longitud_char) <= 100) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {alfanumerico}
          {validationAlert}
        </Box>
      )
    } else {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {textArea}
          {validationAlert}
        </Box>
      )
    }
  }
}

export default TextComponent
